import { generatePath } from 'react-router-dom';

import styled from 'styled-components';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { FormLabel } from '@components/form-label';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useGetTravellerJourneySource } from 'api/traveller-journeys/hooks';
import { TravellerJourneySourceJourney } from 'api/traveller-journeys/types';
import { routePaths } from 'ui/authenticated/navigation';

type AddedFromJourneySidebarProps = {
  travellerJourneyId: string;
};

export const useAddedFromJourneySidebar = () => {
  return useSidebar(AddedFromJourneySidebar, {
    variant: 'REGULAR',
  });
};

export const AddedFromJourneySidebar = ({ travellerJourneyId }: AddedFromJourneySidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const { data, isLoading, isError } = useGetTravellerJourneySource(travellerJourneyId);

  let content;
  if (isLoading) {
    content = <Sidebar.Spinner />;
  } else if (isError) {
    content = <Text font="regular14" color="textSecondary" text={t('loadFailed')} />;
  } else if (!data) {
    content = <Text font="regular14" color="textSecondary" text={t('noData')} />;
  } else {
    content = (
      <Stack space={6}>
        <Text font="regular14" color="textSecondary" text={t('intro')} />
        <div>
          <FormLabel text={t('date')} />
          <Text font="regular14" text={abbreviateDate(data.dateCreated, 'en', 'datetime')} />
        </div>
        <SourceJourneyCard sourceJourney={data.sourceInfo.sourceJourney} />
      </Stack>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconJourneyItem}
        iconColor="elementPurple100"
        title={t('title')}
      />
      <Sidebar.Content>{content}</Sidebar.Content>
      <Sidebar.Footer>
        <ButtonGroup align="right">
          <Button text={t('core.close')} variant="secondary" onClick={hideSidebar} />
        </ButtonGroup>
      </Sidebar.Footer>
    </Sidebar>
  );
};

type SourceJourneyCardProps = {
  sourceJourney: TravellerJourneySourceJourney | null;
};

export const SourceJourneyCard = ({ sourceJourney }: SourceJourneyCardProps) => {
  const { t } = useScopedTranslate(locale);

  if (sourceJourney?.access === 'accessible') {
    const journeyUrl = sourceJourney.workspaceId
      ? generatePath(routePaths.journeyDetails, {
          workspaceId: sourceJourney.workspaceId,
          journeyId: sourceJourney.shortId,
          '*': '',
        })
      : null;
    const hasDescription = Boolean(sourceJourney.description);

    const card = (
      <Card
        spaceX={4}
        spaceY={3}
        interactive={Boolean(journeyUrl)}
        dataTestId="source-journey-card"
      >
        <Stack space={2} orientation="horizontal" align="center" justify="space-between">
          <Stack space={4} align="center" orientation="horizontal">
            <IconJourneyItem color="elementPurple80" />
            <TitleContainer>
              <Text text={sourceJourney.title} font="semibold14" />
              <Text
                text={hasDescription ? sourceJourney.description : t('noDescription')}
                font="regular14"
                color={hasDescription ? 'textSecondary' : 'textHelper'}
              />
            </TitleContainer>
          </Stack>
          {journeyUrl ? <IconOpenNewTab size={16} color="textHelper" /> : null}
        </Stack>
      </Card>
    );

    if (!journeyUrl) {
      return card;
    }

    return (
      <CardLink href={journeyUrl} target="_blank" rel="noopener noreferrer">
        {card}
      </CardLink>
    );
  }

  const isPrivate = sourceJourney?.access === 'private';

  return (
    <Card spaceX={4} spaceY={3} variant="gray" dataTestId="source-journey-card">
      <Stack space={4} align="center" orientation="horizontal">
        <IconJourneyItem color="textHelper" />
        <div>
          <Text
            text={isPrivate ? t('noAccess') : t('unavailableTitle')}
            font="semibold14"
            color="textHelper"
          />
          <Text
            text={isPrivate ? t('noAccessDescription') : t('unavailableDescription')}
            font="regular14"
            color="textHelper"
          />
        </div>
      </Stack>
    </Card>
  );
};

const CardLink = styled.a({
  display: 'block',
  color: 'inherit',
  textDecoration: 'none',
});

const TitleContainer = styled.div({
  minWidth: 0,
});

const locale = {
  en: {
    title: 'Added from another journey',
    intro:
      'This traveller was added to this journey automatically through an "Add traveller to Journey" action in another journey workflow.',
    date: 'Date',
    noDescription: 'No description',
    noAccess: '[No access]',
    noAccessDescription: 'Private (you do not have access to this journey)',
    unavailableTitle: 'Journey no longer available',
    unavailableDescription: 'This journey is no longer available',
    noData: 'No data available for this trigger.',
    loadFailed: "We couldn't load this trigger.",
  },
};
